import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import frameImage from '../assets/PDFexport.png';

const i18n = defineMessages({
	printCoverHeader: {
		id: 'pdf-print-utils.print-cover.header',
		defaultMessage: 'Your page may not be formatted properly if you print now.',
		description: 'Header warning for print cover page',
	},
	printCoverSubheader: {
		id: 'pdf-print-utils.print-cover.subheader',
		defaultMessage:
			"To maintain your page\'s format, export it as a PDF before printing - either with ⌘P/Ctrl+P or by following these steps:",
		description: 'Subheader instructions for print cover page',
	},
	printStep1: {
		id: 'pdf-print-utils.print-cover.step1',
		defaultMessage: 'Close this window',
		description: 'First step in print instructions',
	},
	printStep2: {
		id: 'pdf-print-utils.print-cover.step2',
		defaultMessage: 'Select More actions (•••) in the upper right.',
		description: 'Second step in print instructions',
	},
	printStep3: {
		id: 'pdf-print-utils.print-cover.step3',
		defaultMessage: 'Select Export > Export as PDF.',
		description: 'Third step in print instructions',
	},
	printStep4: {
		id: 'pdf-print-utils.print-cover.step4',
		defaultMessage: 'Download and open the PDF to print.',
		description: 'Fourth step in print instructions',
	},
	imageAlt: {
		id: 'pdf-print-utils.print-cover.image-alt',
		defaultMessage: 'PDF export instructions diagram',
		description: 'Alt text for the PDF export instructions image',
	},
});

const coverWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	width: '100%',
	padding: 'space.500',
	boxSizing: 'border-box',
	marginTop: 'space.100',
});

const headingStyles = css({
	//eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '35px',
	fontWeight: token('font.weight.medium'),
	color: token('color.text.accent.blue.bolder'),
});

const subHeadingStyles = css({
	//eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '26px',
	marginTop: token('space.300'),
	fontWeight: token('font.weight.medium'),
	color: token('color.text.accent.blue.bolder'),
});

const imgContStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.100',
	borderRadius: 'radius.large',
	overflow: 'hidden',
	boxShadow: 'elevation.shadow.overlay',
});

const imageStyles = css({ width: '100%', display: 'block' });

const listStyles = css({
	marginTop: token('space.100'),
	paddingInlineStart: token('space.400'),
});

const listItemStyles = css({
	//eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '23px',
	fontWWeight: token('font.weight.medium'),
	marginTop: token('space.250'),
	lineSpacing: '14',
	color: token('color.text.accent.blue.bolder'),
});

export const CoverPageComponent = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<Box xcss={coverWrapperStyles}>
			{/* Using Text here instead of Heading to avoid loading @atlaskit/heading css */}
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
			<h1 css={headingStyles}>
				<FormattedMessage {...i18n.printCoverHeader} />
			</h1>
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
			<h1 css={subHeadingStyles}>
				<FormattedMessage {...i18n.printCoverSubheader} />
			</h1>
			<Box xcss={imgContStyles}>
				<img alt={intl.formatMessage(i18n.imageAlt)} src={frameImage} css={imageStyles} />
			</Box>
			<ol css={listStyles}>
				<li css={listItemStyles}>
					<FormattedMessage {...i18n.printStep1} />
				</li>
				<li css={listItemStyles}>
					<FormattedMessage {...i18n.printStep2} />
				</li>
				<li css={listItemStyles}>
					<FormattedMessage {...i18n.printStep3} />
				</li>
				<li css={listItemStyles}>
					<FormattedMessage {...i18n.printStep4} />
				</li>
			</ol>
		</Box>
	);
};

export { i18n as printCoverMessages };
